/**
 * PublishChecklist — "Before you publish" on Review & Publish (Wisconsin).
 *
 * Lists what the publish gate checks, in the gate's order, so the seller can
 * see what is left instead of reading a disabled button's tooltip. It also
 * hosts the required supplemental uploads — today the interim Wisconsin
 * § 709.02(2) condominium addendum (user decision: until the structured form
 * ships, a WI condo seller uploads a completed addendum before publishing).
 *
 * The addendum is the seller's own statement, so the seller signs it: legal
 * L4 §9.3 made the attestation REQUIRED. The file and the attestation (a
 * checkbox, the typed name and the current version) go up in one request,
 * the same way the condo documents panel collects its attestations. A file
 * uploaded before that rule stays stored but doesn't count until it is
 * uploaded again with the attestation (there is no separate "attest" call).
 *
 * Props:
 *   - items: [{key, label, done, detail?}] in display order
 *   - uploads: [{code, label, help, houseId, uploaded, attested,
 *       attestationRequired, attestationText, attestationVersion,
 *       originalFilename, uploadedAt}] required uploads for this listing
 *   - onUploaded(code, result): an upload succeeded
 */
import React, { useRef, useState } from 'react';
import {
  MAX_REQUIRED_UPLOAD_BYTES,
  REQUIRED_UPLOAD_ACCEPT,
  RequiredUploadsAPI,
  UPLOAD_ATTESTATION_STALE_CODE,
  UPLOAD_INCOMPLETE_CODE,
  isUploadComplete,
} from '../../agents/requiredUploadsAgent';
import styles from './PublishChecklist.module.css';

/** Mirrors the server's minimum typed-signature length (uploads.py). */
const MIN_SIGNATURE_LENGTH = 2;

/** UI chrome shared with the condo documents panel's attestations. */
const ATTESTATION_CHECKBOX = 'I confirm the statement above.';
const SIGNATURE_LABEL = 'Type your full name to sign';

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const fileLine = (upload) =>
  `${upload.originalFilename ? `: ${upload.originalFilename}` : ''}${
    upload.uploadedAt ? ` (${formatDate(upload.uploadedAt)})` : ''
  }`;

const normalizedName = (name) => name.split(/\s+/).filter(Boolean).join(' ');

/** A seller-facing message for a refused upload. */
function uploadErrorMessage(err) {
  const code = err?.detail?.code;
  if (code === UPLOAD_INCOMPLETE_CODE) {
    return 'Check the box and type your full name to sign the statement, then upload again.';
  }
  if (code === UPLOAD_ATTESTATION_STALE_CODE) {
    return 'The statement has been updated. Reload the page to read the current statement, then sign and upload again.';
  }
  return err?.message || "We couldn't upload that file. Please try again.";
}

function RequiredUpload({ upload, onUploaded }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [certify, setCertify] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputId = `required-upload-${upload.code}`;
  const helpId = `${inputId}-help`;
  const signId = `${inputId}-sign`;
  const statementId = `${inputId}-statement`;

  const needsAttestation = upload.attestationRequired !== false;
  const complete = isUploadComplete(upload);
  // Uploaded before the attestation was required, or under an older version.
  const unattested = Boolean(upload.uploaded) && !complete;
  const signed = certify && normalizedName(signatureName).length >= MIN_SIGNATURE_LENGTH;
  const canSubmit = Boolean(file) && signed && !loading;

  const send = async (chosen) => {
    if (!upload.houseId) {
      setError('Save your listing as a draft before uploading.');
      return;
    }
    const attestation = needsAttestation
      ? {
          certify: true,
          signatureName: normalizedName(signatureName),
          attestationVersion: upload.attestationVersion || '',
        }
      : undefined;
    setLoading(true);
    setError(null);
    try {
      const result = await RequiredUploadsAPI.upload(upload.houseId, upload.code, chosen, attestation);
      onUploaded(upload.code, {
        uploaded: true,
        // The server says whether it counts; without the field, what we sent.
        attested: typeof result?.attested === 'boolean' ? result.attested : Boolean(attestation),
        attestationRequired: needsAttestation,
        originalFilename: result?.original_filename || chosen.name,
        uploadedAt: result?.uploaded_at || new Date().toISOString(),
      });
      setFile(null);
      setCertify(false);
      setSignatureName('');
    } catch (err) {
      console.error(`Required upload ${upload.code} failed:`, err);
      setError(uploadErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleFile = (e) => {
    const chosen = e.target.files?.[0];
    e.target.value = '';
    if (!chosen) return;
    if (chosen.size > MAX_REQUIRED_UPLOAD_BYTES) {
      setError('That file is larger than 25 MB. Upload a smaller copy.');
      return;
    }
    setError(null);
    // Without an attestation to sign, the file goes up as soon as it's chosen.
    if (!needsAttestation) {
      send(chosen);
      return;
    }
    setFile(chosen);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    send(file);
  };

  let chooseLabel = 'Choose file';
  if (file) chooseLabel = 'Choose a different file';
  else if (upload.uploaded) chooseLabel = 'Replace file';
  if (!needsAttestation) {
    chooseLabel = loading ? 'Uploading…' : upload.uploaded ? 'Replace file' : 'Upload file';
  }

  return (
    <div className={styles.upload} data-testid={`required-upload-${upload.code}`}>
      <p id={helpId} className={styles.detail}>
        {upload.help}
      </p>
      {complete && (
        <p className={styles.uploaded} data-testid={`required-upload-${upload.code}-done`}>
          {needsAttestation ? 'Uploaded and signed' : 'Uploaded'}
          {fileLine(upload)}
        </p>
      )}
      {unattested && (
        <p className={styles.unattested} data-testid={`required-upload-${upload.code}-unattested`}>
          Uploaded{fileLine(upload)}, but not signed. Upload the file again and sign the
          statement below before publishing.
        </p>
      )}
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={REQUIRED_UPLOAD_ACCEPT}
        onChange={handleFile}
        className={styles.fileInput}
        aria-describedby={helpId}
        data-testid={`required-upload-${upload.code}-input`}
      />
      <button
        type="button"
        className={styles.uploadButton}
        onClick={() => inputRef.current?.click()}
        disabled={loading}
        aria-busy={loading}
        aria-describedby={helpId}
      >
        {chooseLabel}
      </button>
      {file && (
        <p className={styles.selected} data-testid={`required-upload-${upload.code}-selected`}>
          Selected: {file.name}
        </p>
      )}

      {needsAttestation && (file || !complete) && (
        <form
          className={styles.attestation}
          onSubmit={handleSubmit}
          data-testid={`required-upload-${upload.code}-attestation`}
          aria-label={`Sign the ${upload.label || 'document'}`}
        >
          <blockquote id={statementId} className={styles.attestationQuote}>
            {upload.attestationText}
          </blockquote>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={certify}
              onChange={(e) => setCertify(e.target.checked)}
              disabled={loading}
              data-testid={`required-upload-${upload.code}-certify`}
            />
            <span>{ATTESTATION_CHECKBOX}</span>
          </label>
          <label className={styles.fieldLabel} htmlFor={signId}>
            {SIGNATURE_LABEL}
          </label>
          <input
            id={signId}
            type="text"
            className={styles.textInput}
            value={signatureName}
            autoComplete="name"
            onChange={(e) => setSignatureName(e.target.value)}
            disabled={loading}
            data-testid={`required-upload-${upload.code}-signature`}
          />
          {!file && (
            <p className={styles.hint}>Choose the completed file above, then sign.</p>
          )}
          <button
            type="submit"
            className={styles.submitButton}
            disabled={!canSubmit}
            aria-busy={loading}
            data-testid={`required-upload-${upload.code}-submit`}
          >
            {loading ? 'Uploading…' : 'Upload and sign'}
          </button>
        </form>
      )}

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export default function PublishChecklist({ items = [], uploads = [], onUploaded }) {
  const uploadItems = uploads.map((u) => ({
    key: `upload-${u.code}`,
    label: u.label,
    done: isUploadComplete(u),
    upload: u,
  }));
  const all = [...items, ...uploadItems];
  return (
    <section className={styles.checklist} aria-labelledby="publish-checklist-title" data-testid="publish-checklist">
      <h4 id="publish-checklist-title" className={styles.title}>
        Before you publish
      </h4>
      <ul className={styles.list}>
        {all.map((item) => (
          <li
            key={item.key}
            className={`${styles.item} ${item.done ? styles.itemDone : ''}`}
            data-testid={`publish-checklist-${item.key}`}
            data-done={item.done ? 'true' : 'false'}
          >
            <span className={styles.mark} aria-hidden="true">
              {item.done ? '✓' : ''}
            </span>
            <div className={styles.body}>
              <p className={styles.label}>
                {item.label}
                <span className={styles.srOnly}>{item.done ? ' (done)' : ' (to do)'}</span>
              </p>
              {item.detail && <p className={styles.detail}>{item.detail}</p>}
              {item.upload && <RequiredUpload upload={item.upload} onUploaded={onUploaded} />}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
